import { formatModelFollowedItem, formatPaginatedResponseItems, formatResponseFollowedItem, returnPaginatedResponse } from "./format";
import useRequest from "~/hooks/use-request";
import useUserStore from "~/stores/user";
import CategoriesAPI from "~/services/categories";
import { ORDER, SORT } from "~/constants";

const FollowAPI = {
  load(options?: IRequestPaginatedParams): Promise<IModelFollowedItem[]> {
    const { request } = useRequest();
    const userStore = useUserStore();

    const defaultParams = {
      size: 150,
      version: 2,
    };

    const params = Object.assign({}, defaultParams, options);

    return request
      .get<IPaginatedResponse<IResponseFollowedItem<IResponseFollowedItemEntity>>>("/api/follow/list_by_user", { query: params }).then((response) => {
        const followedItems = response.content.entries.map(formatResponseFollowedItem);
        followedItems.forEach(item => userStore.followed.set(item.id, item));

        return followedItems;
      });
  },

  loadByType(options?: IRequestPaginatedParams & { type: TFollowType }): Promise<IPaginatedResponse<IModelFollowedItem>> {
    const { request } = useRequest();
    const defaultParams = {
      size: 150,
      version: 2,
    };

    const params = Object.assign({}, defaultParams, options);
    return request
      .get<IPaginatedResponse<IResponseFollowedItem<IResponseFollowedItemEntity>>>("/api/follow/list_by_user", { query: params })
      .then(response => formatPaginatedResponseItems(response, formatResponseFollowedItem));
  },

  loadCache(options: IRequestPaginatedOptions & { identityId: string }): Promise<IPaginatedResponse<IModelFollowedItem>> {
    const userStore = useUserStore();
    const followedIdentityItems = userStore.followedIdentityItems;

    if (options.identityId) {
      return Promise.resolve(
        returnPaginatedResponse(
          followedIdentityItems.filter(item => item.identitiesId?.includes(options.identityId)),
        ),
      );
    }

    return Promise.resolve(returnPaginatedResponse(followedIdentityItems));
  },

  async loadGroupedByType(): Promise<IModelFollowedItemGroupedByType[]> {
    const userStore = useUserStore();
    const runtimeConfig = useRuntimeConfig().public;
    const isLeaguesView = runtimeConfig.APP_CUSTOMER.enable_leagues_view;
    let groupedItems: IModelFollowedItemGroupedByType[];

    if (isLeaguesView) {
      groupedItems = [
        { title: "teams", followedItems: userStore.followedTeams },
        { title: "league", followedItems: userStore.followedIdentities },
      ];
    } else {
      const identities = await CategoriesAPI.load({ page: 0, pageSize: 50, order: ORDER.asc, sort: SORT.order }).then(response => response.content.entries);
      groupedItems = userStore.followedIdentitiesIds.reduce((acc: IModelFollowedItemGroupedByType[], curr) => {
        const title = identities.find(c => c.id === curr)?.title;
        if (!title)
          return acc;

        const item = { id: curr, title, followedItems: userStore.followedIdentityItems.filter(s => s.identitiesId?.includes(curr)) };

        return [...acc, item];
      }, []);
    }
    return groupedItems;
  },

  follow(options: { item: TFollowItemType; type: TFollowType }): Promise<void> {
    const { request } = useRequest();
    const { item, type } = options;
    const params = { reference_id: item.id, type };
    const userStore = useUserStore();
    const followItem = formatModelFollowedItem(item, type);

    return request
      .post("/api/follow/save", { body: params }).then(() => {
        userStore.followed.set(followItem.id, followItem);
      });
  },
  unfollow(id: string): Promise<void> {
    const { request } = useRequest();
    const params = { id };
    const userStore = useUserStore();

    return request
      .post("/api/follow/remove_by_reference", { body: params }).then(() => {
        userStore.followed.delete(id);
      });
  },

};

export default FollowAPI;
